import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import { TrendingUp, Search, Download, FileText, CalendarDays, IndianRupee, X } from "lucide-react";
import { toast } from "sonner";

const PAYMENT_MODES = ["All", "Cash", "Online", "Cheque", "UPI", "Bank Transfer"];

const INR = (n) => {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n || 0));
  } catch { return `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`; }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

function currentSession() {
  const now = new Date();
  const y = now.getFullYear();
  const start = now.getMonth() >= 3 ? y : y - 1; // April rollover
  return `${start}-${start + 1}`;
}

function sessionRange(label) {
  if (!label) return { from: "", to: "" };
  const [s, e] = label.split("-").map(Number);
  return { from: `${s}-04-01`, to: `${e}-03-31` };
}

export default function ReportsPanel({ can }) {
  const session = currentSession();
  const today = new Date().toISOString().slice(0, 10);
  const seed = sessionRange(session);

  const [filters, setFilters] = useState({
    student_name: "",
    payment_mode: "All",
    from_date: seed.from,
    to_date: today,
    date_type: "collection",
  });
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = async (override) => {
    setLoading(true);
    try {
      const f = override || filters;
      const params = {};
      if (f.student_name) params.student_name = f.student_name;
      if (f.payment_mode && f.payment_mode !== "All") params.payment_mode = f.payment_mode;
      if (f.from_date) params.from_date = f.from_date;
      if (f.to_date) params.to_date = f.to_date;
      params.date_type = f.date_type;
      params.session = session;
      const { data } = await adminApi.get("/admin/reports/fees-transactions", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const onSearch = (e) => { e?.preventDefault?.(); load(); };

  const onExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.student_name) params.set("student_name", filters.student_name);
      if (filters.payment_mode && filters.payment_mode !== "All") params.set("payment_mode", filters.payment_mode);
      if (filters.from_date) params.set("from_date", filters.from_date);
      if (filters.to_date) params.set("to_date", filters.to_date);
      params.set("date_type", filters.date_type);
      params.set("session", session);
      params.set("fmt", "csv");
      const { data } = await adminApi.get(`/admin/reports/fees-transactions?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fees_transactions_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    } finally { setExporting(false); }
  };

  const downloadReceipt = async (receiptId, receiptNo) => {
    try {
      const { data } = await adminApi.get(`/admin/receipts/${receiptId}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error("Could not load receipt PDF");
    }
  };

  const set = (k) => (v) => setFilters((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-6" data-testid="reports-fees-panel">
      <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden">
        <div className="p-6 md:p-7 border-b border-[#0F1B47]/8 flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-[#648DE5]/15 text-[#0F1B47] flex items-center justify-center">
              <TrendingUp size={18} />
            </div>
            <div>
              <h2 className="font-display text-2xl text-[#0F1B47] font-medium">Fees transactions</h2>
              <p className="text-sm text-[#4A5568] mt-0.5 max-w-2xl">All paid receipts in the selected window — online, cash, UPI, NEFT and cheque. Use this for daily reconciliation and reporting.</p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">Session</div>
            <div className="text-base font-semibold text-[#0F1B47]">{session}</div>
          </div>
        </div>

        <form onSubmit={onSearch} className="p-6 md:p-7 border-b border-[#0F1B47]/8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="reports-filter-form">
          <FilterField label="Student name / ID">
            <input
              value={filters.student_name}
              onChange={(e) => set("student_name")(e.target.value)}
              placeholder="Type name or admission ID"
              className={inputCls}
              data-testid="reports-filter-name"
            />
          </FilterField>
          <FilterField label="Payment mode">
            <select
              value={filters.payment_mode}
              onChange={(e) => set("payment_mode")(e.target.value)}
              className={inputCls}
              data-testid="reports-filter-mode"
            >
              {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </FilterField>
          <FilterField label="From date">
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => set("from_date")(e.target.value)}
              className={inputCls}
              data-testid="reports-filter-from"
            />
          </FilterField>
          <FilterField label="To date">
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => set("to_date")(e.target.value)}
              className={inputCls}
              data-testid="reports-filter-to"
            />
          </FilterField>
          <FilterField label="Date type">
            <select
              value={filters.date_type}
              onChange={(e) => set("date_type")(e.target.value)}
              className={inputCls}
              data-testid="reports-filter-date-type"
            >
              <option value="collection">Collection date</option>
              <option value="created">Created at (audit)</option>
            </select>
          </FilterField>
          <div className="lg:col-span-3 flex items-end gap-2 flex-wrap">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-[#0F1B47] text-[#FDFDF9] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#1a2b6b] disabled:opacity-50"
              data-testid="reports-search-btn"
            >
              <Search size={14} /> {loading ? "Loading…" : "Search"}
            </button>
            <button
              type="button"
              onClick={onExport}
              disabled={exporting || rows.length === 0}
              className="inline-flex items-center gap-2 bg-[#C6D947] text-[#0F1B47] rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-[#d6e860] disabled:opacity-50"
              data-testid="reports-export-btn"
            >
              <Download size={14} /> {exporting ? "Preparing…" : "Export CSV"}
            </button>
            {(filters.student_name || filters.payment_mode !== "All" || filters.from_date !== seed.from || filters.to_date !== today || filters.date_type !== "collection") && (
              <button
                type="button"
                onClick={() => {
                  const defaults = { student_name: "", payment_mode: "All", from_date: seed.from, to_date: today, date_type: "collection" };
                  setFilters(defaults);
                  load(defaults);
                }}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#0F1B47] px-3 py-2 rounded-full hover:bg-[#0F1B47]/5"
                data-testid="reports-reset-btn"
              >
                <X size={12} /> Reset filters
              </button>
            )}
          </div>
        </form>

        {/* Total banner */}
        <div className="px-6 md:px-7 py-4 bg-[#648DE5]/10 flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-[#0F1B47]/8">
          <div className="text-sm font-semibold text-[#0F1B47]" data-testid="reports-total-records">
            Total <span className="text-[#648DE5]">({rows.length} records)</span>
          </div>
          <div className="text-xl font-bold text-[#0F1B47] flex items-center gap-1" data-testid="reports-total-amount">
            <IndianRupee size={18} /> {INR(total).replace("₹", "")}
          </div>
        </div>

        <div className="p-2 md:p-4">
          {loading ? (
            <div className="text-sm text-[#718096] py-12 text-center">Loading transactions…</div>
          ) : rows.length === 0 ? (
            <div className="bg-[#F5F5F0] rounded-2xl p-12 text-center">
              <TrendingUp size={28} className="mx-auto text-[#648DE5] mb-2" />
              <div className="text-[#0F1B47] font-medium">No fees transactions in this window</div>
              <p className="text-xs text-[#718096] mt-1">Try widening the date range or clearing filters.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="reports-table">
                <thead className="bg-[#0F1B47]/5 text-[#0F1B47]">
                  <tr className="text-[10px] font-bold tracking-[0.16em] uppercase">
                    <th className="px-4 py-3 text-left">Student</th>
                    <th className="px-4 py-3 text-left">Mode</th>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3 text-right">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-t border-[#0F1B47]/8" data-testid={`reports-row-${r.id}`}>
                      <td className="px-4 py-3 align-top">
                        <div className="font-semibold text-[#0F1B47]">{r.student_name || "—"}</div>
                        <div className="text-[11px] text-[#4A5568] mt-0.5">{r.admission_id}{r.student_class ? ` · ${r.student_class}` : ""}</div>
                        {r.period && <div className="text-[11px] text-[#718096] mt-0.5">{r.period}{r.head_name ? ` · ${r.head_name}` : ""}</div>}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="text-[13px] font-medium text-[#0F1B47]">{r.payment_mode || "—"}</div>
                        {r.ref_no && <div className="text-[10px] text-[#718096] mt-0.5 truncate max-w-[140px]" title={r.ref_no}>{r.ref_no}</div>}
                      </td>
                      <td className="px-4 py-3 align-top text-[13px] text-[#0F1B47] whitespace-nowrap">
                        {fmtDate(r.date)}
                        <div className="text-[10px] text-[#718096] mt-0.5">by {r.collected_by ? r.collected_by.split("@")[0] : "—"}</div>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className="inline-flex items-center text-[10px] font-bold tracking-[0.14em] uppercase rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <div className="font-display text-base font-semibold text-[#0F1B47]">{INR(r.amount)}</div>
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        {r.receipt_id ? (
                          <button
                            onClick={() => downloadReceipt(r.receipt_id, r.receipt_no)}
                            className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#0F1B47] px-3 py-1.5 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10"
                            data-testid={`reports-invoice-${r.id}`}
                          >
                            <FileText size={12} /> {r.receipt_no || "Invoice"}
                          </button>
                        ) : (
                          <span className="text-[11px] text-[#718096]">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14px] text-[#0F1B47] placeholder:text-[#718096] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9]";

function FilterField({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
